import React, { useState, useEffect } from 'react';
import { Button, Flex, Text, Input, InputGroup, Box } from '@chakra-ui/react';
import PropTypes from 'prop-types';

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

const CommonPagination = ({
  onPageChange,
  pageCount = 0,
  activePage,
  totalCount,
  itemsPerPage,
  onItemsPerPageChange,
}) => {
  const [jumpPage, setJumpPage] = useState('');
  // Local state to immediately reflect the selected value in the dropdown
  const [selectedLimit, setSelectedLimit] = useState(itemsPerPage);

  // Sync local state when itemsPerPage prop changes (e.g., from URL)
  useEffect(() => {
    setSelectedLimit(itemsPerPage);
  }, [itemsPerPage]);

  const handlePageChange = (page) => {
    if (page >= 1 && page <= pageCount) {
      onPageChange(page);
    }
  };

  const handleJump = () => {
    const pageNum = parseInt(jumpPage, 10);

    if (pageNum >= 1 && pageNum <= pageCount) {
      handlePageChange(pageNum);
      setJumpPage('');
    } else {
      setJumpPage('');
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleJump();
    }
  };

  const handleInputChange = (e) => {
    let value = e.target.value;
    // Only allow numbers
    value = value.replace(/[^0-9]/g, '');
    setJumpPage(value);
  };

  // eslint-disable-next-line no-mixed-operators
  const startItem = (activePage - 1) * itemsPerPage + 1;
  const endItem = Math.min(activePage * itemsPerPage, totalCount);

  return (
    <Flex
      alignItems={{ base: 'center', md: 'center' }}
      justifyContent="space-between"
      gap={4}
      mt={4}
      w="full"
      ml={3}
      flexWrap={'wrap'}
    >
      <Text fontSize="md" color="gray.600" fontWeight="bold" flexShrink={0}>
        Viewing {startItem} - {endItem} of {totalCount} results
      </Text>

      {/* Right side: Pagination buttons and Jump to input */}
      <Flex alignItems="center" gap={3}>
        {/* Page Size Selector */}
        <Flex alignItems="center" gap={2}>
          <Text fontSize="md" color="gray.600" fontWeight="bold" flexShrink={0}>
            Show:
          </Text>
          <Box
            as="select"
            value={selectedLimit}
            onChange={(e) => {
              const newValue = Number(e.target.value);
              setSelectedLimit(newValue);
              if (onItemsPerPageChange) {
                onItemsPerPageChange(newValue);
              }
            }}
            w="80px"
            h="40px"
            pl={3}
            pr={3}
            borderRadius="lg"
            border="1px solid"
            borderColor="gray.300"
            bg="white"
            fontSize="md"
            fontWeight="medium"
            color="gray.700"
            cursor="pointer"
            _hover={{ borderColor: 'blue.400' }}
            _focus={{
              borderColor: 'blue.500',
              boxShadow: '0 0 0 1px var(--chakra-colors-blue-500)',
              outline: 'none',
            }}
          >
            {PAGE_SIZE_OPTIONS.map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </Box>
        </Flex>

        <Flex alignItems="center" gap={2}>
          <Button
            onClick={() => handlePageChange(activePage - 1)}
            isDisabled={activePage === 1}
            aria-label="Previous Page"
            size="md"
            px={4}
            py={2}
            variant="outline"
          >
            Previous
          </Button>
          <Text fontSize="md" color="gray.600" fontWeight="medium" mx={1}>
            {activePage}/{pageCount}
          </Text>
          <Button
            onClick={() => handlePageChange(activePage + 1)}
            isDisabled={activePage === pageCount}
            aria-label="Next Page"
            size="md"
            px={4}
            py={2}
            variant="outline"
          >
            Next
          </Button>
        </Flex>

        {/* Page Jump Input */}
        <Flex alignItems="center" gap={2}>
          <Text fontSize="md" color="gray.600" fontWeight="bold" flexShrink={0}>
            Go to:
          </Text>
          <InputGroup size="sm" w="70px">
            <Input
              type="text"
              placeholder={`1-${pageCount}`}
              value={jumpPage}
              onChange={handleInputChange}
              onKeyPress={handleKeyPress}
              borderRadius="lg"
              textAlign="center"
              border="1px solid"
              borderColor="gray.300"
              _hover={{ borderColor: 'blue.400' }}
              _focus={{
                borderColor: 'blue.500',
                boxShadow: '0 0 0 1px var(--chakra-colors-blue-500)',
              }}
              fontWeight="medium"
            />
          </InputGroup>
          <Button
            size="md"
            onClick={handleJump}
            variant="outline"
            px={4}
            isDisabled={
              !jumpPage ||
              parseInt(jumpPage, 10) < 1 ||
              parseInt(jumpPage, 10) > pageCount
            }
            _disabled={{ opacity: 0.5, cursor: 'not-allowed' }}
          >
            Go
          </Button>
        </Flex>
      </Flex>
    </Flex>
  );
};

CommonPagination.propTypes = {
  onPageChange: PropTypes.func.isRequired,
  pageCount: PropTypes.number.isRequired,
  activePage: PropTypes.number.isRequired,
  itemsPerPage: PropTypes.number.isRequired,
  totalCount: PropTypes.number.isRequired,
  onItemsPerPageChange: PropTypes.func,
};

export default CommonPagination;
